import {Badge, Box, Card, CardBody, Flex, FormControl, Stack, Textarea, useToast} from '@chakra-ui/react';
import PinFolderCreateForm from '../../PinFolderCreateForm';
import PinEditFoldersList from '../../PinEditFoldersList';
import {
  folderEditFormSaveRequest,
  folderEditFormSet,
  pinSaveRequest,
  pinsBalanceRequest,
} from '../../../../reducers/pinsReducer';
import ButtonMinor from '../../../../components/ButtonMinor';
import {sendDragWithinPage} from '../../../../vuplex';
import UpgradeButton from '../../UpgradeButton';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLocalStorageState from '../../../../hooks/useLocalStorageState';
import _ from 'lodash';
import axios from 'axios';
import {useRetryOnAccessTokenRefresh} from '../../../../hooks/useRetryOnAccessTokenRefresh';
import {useEnterKey} from '../../../../hooks/useEnterKey';

const PanoToastSaveForm = ({
  onCloseClick,
}) => {
  const panoId = useSelector(s => s.inApp.panoId);
  const panoIdIsApi2025 = useSelector(s => s.inApp.panoIdIsApi2025);
  const panoLocation = useSelector(s => s.inApp.panoLocation);
  const userId = useSelector(s => s.inApp.userId);
  const dispatch = useDispatch();

  const {_id: worldId, rules} = useSelector(s => s.inApp.world) || {};
  const {isPrivate: isPrivateWorld, isSolo: isSoloWorld} = rules || {};

  const [suggestedLabel, setSuggestedLabel] = useState('');
  const [suggestedLabelIsLoading, setSuggestedLabelIsLoading] = useState(false);
  const [pinLabel, setPinLabel] = useState('');
  const [recentFolderId, setRecentFolderId] = useLocalStorageState('pinSaveRecentFolder', null);
  const [selectedFolderId, setSelectedFolderId] = useState(recentFolderId);

  const folderEditForm = useSelector(s => s.pins.folderEditForm);
  const showFolderEditForm = !_.isEmpty(folderEditForm);
  const isFolderFormValid = showFolderEditForm
    ? !!folderEditForm.label
    : !!selectedFolderId;


  useEffect(() => {
    const fetchSuggestedLabel = async () => {
      setSuggestedLabelIsLoading(true);
      const {latitude, longitude} = panoLocation;
      const {data} = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`, {
        headers: {
          'User-Agent': `${userId}/1.0`,
        },
      })
        .catch((e) => {
          console.error(e);
          return {};
        });

      // console.log(data)
      const {display_name} = data || {};
      const suggestedLabel = display_name || '';
      setSuggestedLabel(suggestedLabel);
      setSuggestedLabelIsLoading(false);
      setPinLabel(suggestedLabel);
    };

    if (panoLocation) fetchSuggestedLabel();
  }, [panoLocation]);

  const [isSaving, setIsSaving] = useState(false);
  const canSave = useRef(true);
  const toast = useToast();

  const savePlace = async (folderId) => {
    if (!canSave.current) {
      console.warn('already saved');
      return;
    }

    canSave.current = false;
    setRecentFolderId(folderId);

    const {error, payload} = await dispatch(pinSaveRequest({
      address: suggestedLabel || 'unknown address',
      folderId,
      label: pinLabel,
      panoId,
      panoIdIsApi2025,
      panoLocation,
      suggestedLabel,
    }));

    if (error) {
      toast({
        status: 'error',
        description: 'Unable to save place. Please try again.',
      });
      canSave.current = true;
    } else {
      const {isInPrivateFolder, isInSharedFolder} = payload;
      const showInvisibleWarning = isPrivateWorld
        ? !isSoloWorld && isInPrivateFolder && !isInSharedFolder
        : isInPrivateFolder;

      toast({
        status: 'success',
        description: `Place saved${showInvisibleWarning ? ' (not visible to others in this world)' : ''}`,
      });

      // if (showBalance) {
      //   toast({
      //     status: 'info',
      //     description: pinsBalance === 1
      //       ? `Upgrade to save more places`
      //       : `You may save ${pinsBalance - 1} more ${pinsBalance === 2 ? 'place' : 'places'}`,
      //   });
      // }

      onCloseClick();
    }
  };

  const handleSaveClick = async () => {
    sendDragWithinPage(); // ensure map pan and pinch to zoom work after editing text
    setIsSaving(true);

    if (showFolderEditForm) {
      const {error, payload} = await dispatch(folderEditFormSaveRequest());
      if (error) {
        toast({
          status: 'error',
          description: 'Unable to create Place List. Please try again or pick an existing list.',
        });
      } else {
        await savePlace(payload._id);
      }
    } else {
      await savePlace(selectedFolderId);
    }

    setIsSaving(false);
  };

  useRetryOnAccessTokenRefresh(() => dispatch(pinsBalanceRequest({})));
  const {pinsBalance} = useSelector(s => s.pins.pinsBalanceRequest.response) || {};
  const hasBalance = pinsBalance > 0;
  const showBalance = hasBalance && pinsBalance <= 10;

  const disabledReason = !hasBalance
    ? 'Upgrade to save more places'
    : !pinLabel
      ? 'Please enter a description'
      : !isFolderFormValid
        ? showFolderEditForm
          ? 'Please name your new list'
          : 'Please select a list'
        : null;

  useEnterKey(async () => {
    if (!disabledReason && !showFolderEditForm)
      await handleSaveClick();
  });

  return (
    <Card w="50vw" borderRadius="2xl">
      <CardBody>
        <Stack gap={2}>
          <FormControl isRequired>
            {/*<FormLabel>List</FormLabel>*/}
            {showFolderEditForm
              ? <PinFolderCreateForm/>
              : (
                <PinEditFoldersList
                  onFolderClick={(f) => {
                    if (f) setSelectedFolderId(f._id);
                    else {
                      dispatch(folderEditFormSet({
                        label: '',
                        isSolo: false,
                        sharedInWorld: '',
                      }));
                    }
                  }}
                  selectedFolderId={selectedFolderId}
                />)}
            {/*<FormHelperText>Choose a or create a Place List</FormHelperText>*/}
          </FormControl>

          <Box border="1px solid" borderColor="black24" borderRadius="2xl">
            <FormControl isRequired>
              {/*<FormLabel>Description</FormLabel>*/}
              <Textarea
                border="none"
                boxShadow="none!important"
                isDisabled={suggestedLabelIsLoading}
                placeholder={suggestedLabelIsLoading ? 'Loading...' : 'Describe your saved place...'}
                onChange={(evt) => setPinLabel(evt.target.value)}
                resize="none"
                value={pinLabel}
              />
              {/*<FormHelperText>Add a description</FormHelperText>*/}
            </FormControl>

            <Flex justifyContent="flex-end" gap={2} p={2}>
              <ButtonMinor
                shadow="md"
                onClick={() => {
                  onCloseClick();
                  sendDragWithinPage(); // ensure map pan and pinch to zoom work after editing text
                }}
              >
                Cancel
              </ButtonMinor>

              <ButtonMinor
                disabledReason={disabledReason}
                isLoading={isSaving}
                shadow="md"
                onClick={handleSaveClick}
              >
                Save

                {showBalance && (
                  <Badge
                    bg="wooorldBlue._"
                    borderRadius="3xl"
                    bottom="-0.5em"
                    color="white"
                    paddingX={2}
                    position="absolute"
                    right={0}
                  >
                    {pinsBalance}
                  </Badge>
                )}

                {!hasBalance && (
                  <UpgradeButton
                    descriptor="PinEditForm"
                    top="1.5em"
                    right={0}
                    packageName="explorer+"
                    position="absolute"
                    transform="scale(0.8)"
                    zIndex="sticky"
                  />
                )}
              </ButtonMinor>


            </Flex>
          </Box>


        </Stack>

      </CardBody>

    </Card>

  );
};

export default PanoToastSaveForm;